// Copyright (C) Siemens AG 2026. All rights reserved. Confidential

export interface PricingPlanAPIModel {
    id: number;
    plankey?: string;
    name: string;
    tenantId: number | null;
    neoCreditsPerHour: number;
    neoCreditsPerDay: number;
    simitCreditsPerHour: number;
}

export class PricingPlan {
    id: number;
    plankey?: string;
    name: string;
    tenantId?: number | null;
    uses: number = 0;

    neoCreditsPerHour: number = 0;
    get neoCostsPerHour() { return this.neoCreditsPerHour / CREDITS_TO_COSTS_RATE; }
    set neoCostsPerHour(value: number) { this.neoCreditsPerHour = value * COSTS_TO_CREDITS_RATE; }

    neoCreditsPerDay: number = 0;
    get neoCostsPerDay() { return this.neoCreditsPerDay / CREDITS_TO_COSTS_RATE; }
    set neoCostsPerDay(value: number) { this.neoCreditsPerDay = value * COSTS_TO_CREDITS_RATE; }

    simitCreditsPerHour: number = 0;
    get simitCostsPerHour() { return this.simitCreditsPerHour / CREDITS_TO_COSTS_RATE; }
    set simitCostsPerHour(value: number) { this.simitCreditsPerHour = value * COSTS_TO_CREDITS_RATE; }

    constructor(
        id: number,
        name: string,
        tenantId: number | null,
        neoCreditsPerHour: number,
        neoCreditsPerDay: number,
        simitCreditsPerHour: number,

        from: 'credits' | 'costs' = 'credits',
        additionalParams: Partial<PricingPlan> = {}
    ) {
        this.id = id;
        this.name = name;
        this.tenantId = tenantId;
        this.plankey = additionalParams?.plankey ?? (additionalParams as any)?.planKey ?? (additionalParams as any)?.pricingPlanKey;

        if (from === 'credits') {
            this.neoCreditsPerHour = neoCreditsPerHour;
            this.neoCreditsPerDay = neoCreditsPerDay;
            this.simitCreditsPerHour = simitCreditsPerHour;
            

        } else {
            this.neoCostsPerDay = neoCreditsPerDay;
            this.neoCostsPerHour = neoCreditsPerHour;
            this.simitCostsPerHour = simitCreditsPerHour;
        }
        
        Object.assign(this, additionalParams);
    }

    static FromRaw(apiData: any): PricingPlan {
        return new PricingPlan(
            apiData.id,
            apiData.name,
            apiData.tenantId,
            apiData.neoCreditsPerHour,
            apiData.neoCreditsPerDay,
            apiData.simitCreditsPerHour,
            'credits',
            apiData
        );
    }

    static FromAPI(apiData: PricingPlanAPIModel): PricingPlan {
        return new PricingPlan(
            apiData.id,
            apiData.name,
            apiData.tenantId,
            apiData.neoCreditsPerHour,
            apiData.neoCreditsPerDay,
            apiData.simitCreditsPerHour,
            'credits'
        );
    }

    toCreationAPIForm(): PricingPlanAPIModel {
        return {
            id: this.id,
            plankey: this.plankey,
            name: this.name.trim(),
            tenantId: this.tenantId ?? null,
            neoCreditsPerHour: this.neoCreditsPerHour,
            neoCreditsPerDay: this.neoCreditsPerDay,
            simitCreditsPerHour: this.simitCreditsPerHour,
        };
    }
}

export const CREDITS_TO_COSTS_RATE = 10;
export const COSTS_TO_CREDITS_RATE = 10;


const DEFAULT_PRICING_PLAN_COSTS: PricingPlanAPIModel = {
    id: 0,
    name: 'Default Plan',
    tenantId: null,
    neoCreditsPerHour: 0,
    neoCreditsPerDay: 0,
    simitCreditsPerHour: 0,
};

export function createPricingPlan() {
    return PricingPlan.FromAPI(DEFAULT_PRICING_PLAN_COSTS);
}
